/**
 * Admin 대시보드·사이트·업로드·도움말·커뮤니티 매뉴얼. 라우트 admin.php 와 동기화.
 * data-controller="AdminController", data-action="dashboard" | "components" | "siteIndex" | "help" | "communityManual" | "uploadImage" | "uploadAttachment"
 * - components: DatePicker, QuillEditor 래퍼 사용 (SlimSelect는 initAdminSelects에서 SelectWrapper로 처리)
 * - help / communityManual: .admin__main 스크롤 컨테이너 기준으로 좌·우 목차 GSAP ScrollTrigger pin
 * - communityManual: 탭 전환 및 해시 기반 목차 링크
 */
import { Logger } from "../../utils/Logger";
import { DatePicker } from "../../components/DatePicker";
import { QuillEditor } from "../../components/QuillEditor";

const COMMUNITY_MANUAL_IDS = [
    "comm-flow",
    "comm-notice",
    "comm-fan",
    "comm-exclusive",
    "comm-post",
    "comm-comment",
    "comm-profile",
];

function noop(action: string): void {
    Logger.debug(`AdminController.${action} (no-op)`);
}

function setActiveDocTab(tabId: string, tabs: Element[], panels: Element[]): void {
    tabs.forEach((t) => {
        t.classList.toggle("is-active", t.getAttribute("data-tab") === tabId);
    });
    panels.forEach((p) => {
        p.classList.toggle("is-active", p.getAttribute("data-tab-panel") === tabId);
    });
}

const ADMIN_DOCS_BREAKPOINT = 992;

/**
 * 도움말/매뉴얼 문서 페이지: 스크롤 컨테이너(.admin__main) 기준으로 좌·우 목차를 ScrollTrigger로 pin.
 * cursor-execution-rules: 레이아웃에서 GSAP 로드, 해당 컨트롤러 메소드에서만 pin 초기화.
 * 반응형: 좁은 화면(단일 컬럼)에서는 pin 미적용.
 */
function initDocsScrollPin(): void {
    const scroller = document.querySelector<HTMLElement>(".admin__main");
    const nav = document.querySelector<HTMLElement>(".admin-docs__nav");
    const toc = document.querySelector<HTMLElement>(".admin-docs__toc");
    if (!scroller || !nav || !toc || window.innerWidth < ADMIN_DOCS_BREAKPOINT) return;

    const gsap = (window as Window & { gsap?: { registerPlugin: (p: unknown) => void } }).gsap;
    const ST = (window as Window & { ScrollTrigger?: { create: (config: object) => void } }).ScrollTrigger;
    if (!gsap || !ST) return;
    gsap.registerPlugin(ST);

    ST.create({
        trigger: nav,
        pin: true,
        scroller,
        start: "top top",
        end: "bottom top",
    });
    ST.create({
        trigger: toc,
        pin: true,
        scroller,
        start: "top top",
        end: "bottom top",
    });
}

export const AdminController = {
    dashboard(): void {
        noop("dashboard");
    },
    components(): void {
        const datetimeWrap = document.querySelector<HTMLElement>("[data-datetime-wrap]");
        const datetimeInput = document.getElementById("demo-datetime");
        if (datetimeWrap && datetimeInput) {
            datetimeWrap.addEventListener("click", () => (datetimeInput as HTMLElement).focus());
            new DatePicker("demo-datetime", {
                enableTime: true,
                dateFormat: "Y-m-d H:i",
                time24hr: true,
            }).init();
        }

        const timeWrap = document.querySelector<HTMLElement>("[data-time-wrap]");
        const timeInput = document.getElementById("demo-time");
        if (timeWrap && timeInput) {
            timeWrap.addEventListener("click", () => (timeInput as HTMLElement).focus());
            new DatePicker("demo-time", {
                enableTime: true,
                noCalendar: true,
                dateFormat: "H:i",
                time24hr: true,
            }).init();
        }

        const editorWrap = document.getElementById("demo-editor-wrap");
        if (editorWrap) {
            new QuillEditor(editorWrap, {
                placeholder: "내용을 입력하세요.",
                toolbar: [
                    ["bold", "italic"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                ],
            }).init();
        }
    },
    siteIndex(): void {
        noop("siteIndex");
    },
    help(): void {
        initDocsScrollPin();
    },
    communityManual(): void {
        const tabs = Array.from(document.querySelectorAll(".admin-docs__tab"));
        const panels = Array.from(document.querySelectorAll(".admin-docs__tab-panel"));
        if (!tabs.length || !panels.length) return;

        const checkHash = (): void => {
            const hash = (window.location.hash || "").replace("#", "");
            if (hash === "tab-community" || COMMUNITY_MANUAL_IDS.indexOf(hash) !== -1) {
                setActiveDocTab("community", tabs, panels);
            } else if (hash === "tab-site") {
                setActiveDocTab("site", tabs, panels);
            }
        };

        tabs.forEach((tab) => {
            tab.addEventListener("click", (e) => {
                e.preventDefault();
                const id = tab.getAttribute("data-tab");
                if (id) {
                    setActiveDocTab(id, tabs, panels);
                    const panel = document.getElementById("tab-" + id);
                    if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            });
        });

        document.querySelectorAll(".admin-docs__nav-link[href^='#']").forEach((link) => {
            link.addEventListener("click", () => {
                const href = link.getAttribute("href");
                if (href && href.length > 1) {
                    const id = href.replace("#", "");
                    if (COMMUNITY_MANUAL_IDS.indexOf(id) !== -1) {
                        setActiveDocTab("community", tabs, panels);
                    } else {
                        setActiveDocTab("site", tabs, panels);
                    }
                }
            });
        });

        window.addEventListener("hashchange", checkHash);
        checkHash();
        initDocsScrollPin();
    },
    uploadImage(): void {
        noop("uploadImage");
    },
    uploadAttachment(): void {
        noop("uploadAttachment");
    },
};
